import { useState, useRef, useEffect } from 'react';
import type { CadCategory } from '@shared/types';
import { firestore } from '@/lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import {
  Armchair,
  Cog,
  Building2,
  Printer,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface Props {
  conversationId: string | undefined;
  value: CadCategory | undefined;
  /** Small chip or full dropdown. Chip is for the chat header. */
  variant?: 'chip' | 'dropdown';
}

type Option = {
  value: CadCategory;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
};

const OPTIONS: Option[] = [
  { value: 'furniture', icon: Armchair, accent: 'text-brand-indigo' },
  { value: 'mechanical', icon: Cog, accent: 'text-brand-indigo' },
  { value: 'architecture', icon: Building2, accent: 'text-brand-indigo' },
  { value: 'printing', icon: Printer, accent: 'text-brand-indigo' },
];

export function CategorySelector({
  conversationId,
  value,
  variant = 'chip',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { t } = useTranslation(['intake', 'common']);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDocClick);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const current = OPTIONS.find((o) => o.value === value);

  const setCategory = async (next: CadCategory) => {
    if (!conversationId) return;
    setOpen(false);
    try {
      await updateDoc(doc(firestore, 'conversations', conversationId), {
        category: next,
      });
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
    } catch (e) {
      console.warn('[category] update failed:', e);
    }
  };

  if (!conversationId) return null;

  const Icon = current?.icon ?? Armchair;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border text-xs transition',
          variant === 'chip'
            ? 'border-synergy-neutral-800 bg-synergy-neutral-900/60 hover:border-synergy-neutral-700 px-2.5 py-1'
            : 'border-synergy-neutral-800 bg-synergy-neutral-900 px-3 py-1.5',
        )}
      >
        <Icon className={cn('h-3.5 w-3.5', current?.accent ?? 'text-synergy-neutral-400')} />
        <span className="text-synergy-text-primary">
          {current ? t(`categories.${current.value}.label`, { ns: 'intake' }) : t('unsetCategory', { ns: 'common' })}
        </span>
        <ChevronDown className="h-3 w-3 text-synergy-neutral-500" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-44 rounded-md border border-synergy-neutral-800 bg-synergy-neutral-950 shadow-lg py-1 z-20">
          {OPTIONS.map((opt) => {
            const OptIcon = opt.icon;
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCategory(opt.value)}
                className={cn(
                  'flex w-full items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-synergy-neutral-900',
                  active && 'bg-synergy-neutral-900',
                )}
              >
                <OptIcon className={cn('h-3.5 w-3.5', opt.accent)} />
                <span className="flex-1 text-synergy-text-primary">
                  {t(`categories.${opt.value}.label`, { ns: 'intake' })}
                </span>
                {active && (
                  <span className="text-[10px] text-synergy-neutral-400">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
